import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Image, Upload, FolderOpen, Trash2, Video, Grid, List, Search, FileText, 
  CheckSquare, Square, Copy, Move, Filter, SortAsc, Tag, X, Check
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FOLDERS = ['bronze', 'silver', 'gold', 'platinum', 'secret', 'general', 'guides', 'wallpapers', 'videos', '4k'];
const CATEGORIES = ['all', 'lingerie', 'glamour', 'casual', 'artistic', 'outdoor', 'portrait', 'fullbody', 'video', 'document', 'general'];
const SORT_OPTIONS = ['newest', 'oldest', 'name', 'size', 'category'];

export default function MediaManager() {
  const { t } = useTranslation();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [selectMode, setSelectMode] = useState(false);
  const [bulkAssignDialog, setBulkAssignDialog] = useState(false);
  const [targetFolder, setTargetFolder] = useState('');
  const [copyMode, setCopyMode] = useState(true); // true = copy, false = move
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchMedia();
  }, []);

  const fetchMedia = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/admin/media`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMedia(res.data || []);
    } catch (error) {
      console.error('Failed to fetch media:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files?.length) return;
    
    setUploading(true);
    const folder = selectedFolder === 'all' ? 'general' : selectedFolder;
    let successCount = 0;
    let failCount = 0;
    
    const uploadPromises = Array.from(files).map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = async (event) => {
          try {
            const base64 = event.target.result.split(',')[1];
            const token = localStorage.getItem('token');
            
            await axios.post(`${API}/admin/media/upload`, {
              filename: file.name,
              content_type: file.type,
              base64_data: base64,
              folder: folder
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });
            
            successCount++;
            resolve(true);
          } catch (error) {
            console.error(`Upload failed: ${file.name}`, error);
            failCount++;
            resolve(false);
          }
        };
        reader.onerror = () => {
          failCount++;
          resolve(false);
        };
        reader.readAsDataURL(file);
      });
    });
    
    await Promise.all(uploadPromises);
    
    if (successCount > 0) {
      toast.success(`${successCount} bestand(en) geüpload`);
      fetchMedia();
    }
    if (failCount > 0) {
      toast.error(`${failCount} bestand(en) mislukt`);
    }
    
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const deleteMedia = async (mediaId) => {
    if (!window.confirm(t('admin.media.confirmDelete'))) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/media/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Media verwijderd');
      setMedia(media.filter(m => m.id !== mediaId));
      setSelectedItems(prev => {
        const next = new Set(prev);
        next.delete(mediaId);
        return next;
      });
    } catch (error) {
      toast.error('Verwijderen mislukt');
    }
  };

  const toggleSelectItem = (mediaId) => {
    setSelectedItems(prev => {
      const next = new Set(prev);
      if (next.has(mediaId)) {
        next.delete(mediaId);
      } else {
        next.add(mediaId);
      }
      return next;
    });
  };

  const selectAll = () => {
    setSelectedItems(new Set(filteredMedia.map(m => m.id)));
  };

  const clearSelection = () => {
    setSelectedItems(new Set());
    setSelectMode(false);
  };

  const handleBulkAssign = async () => {
    if (!targetFolder || selectedItems.size === 0) {
      toast.error('Selecteer een map en minimaal één item');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/admin/media/bulk-assign?folder=${encodeURIComponent(targetFolder)}&copy_mode=${copyMode}`,
        Array.from(selectedItems),
        {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      toast.success(`${selectedItems.size} items ${copyMode ? 'gekopieerd naar' : 'verplaatst naar'} ${targetFolder}`);
      setBulkAssignDialog(false);
      clearSelection();
      fetchMedia();
    } catch (error) {
      console.error('Bulk assign failed:', error);
      toast.error('Bulk actie mislukt');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.size === 0) return;
    if (!window.confirm(`${selectedItems.size} items verwijderen? Dit kan niet ongedaan worden.`)) return;

    const token = localStorage.getItem('token');
    let deleted = 0;

    for (const id of selectedItems) {
      try {
        await axios.delete(`${API}/admin/media/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        deleted++;
      } catch (error) {
        console.error(`Failed to delete ${id}`);
      }
    }

    toast.success(`${deleted} items verwijderd`);
    clearSelection();
    fetchMedia();
  };

  // Filter and sort media
  const filteredMedia = media
    .filter(m => {
      const matchesFolder = selectedFolder === 'all' || m.folder === selectedFolder;
      const matchesCategory = selectedCategory === 'all' || m.category === selectedCategory;
      const matchesSearch = !searchQuery || 
        m.filename?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        m.category?.toLowerCase().includes(searchQuery.toLowerCase());
      return matchesFolder && matchesCategory && matchesSearch;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0);
        case 'oldest':
          return new Date(a.created_at || 0) - new Date(b.created_at || 0);
        case 'name':
          return (a.filename || '').localeCompare(b.filename || '');
        case 'size':
          return (b.size || 0) - (a.size || 0);
        case 'category':
          return (a.category || '').localeCompare(b.category || '');
        default:
          return 0;
      }
    });

  const folderCounts = FOLDERS.reduce((acc, folder) => {
    acc[folder] = media.filter(m => m.folder === folder).length;
    return acc;
  }, {});

  const categoryCounts = CATEGORIES.reduce((acc, cat) => {
    acc[cat] = cat === 'all' ? media.length : media.filter(m => m.category === cat).length;
    return acc;
  }, {});

  const renderMediaThumbnail = (item) => {
    const isSelected = selectedItems.has(item.id);
    const isVideo = item.content_type?.startsWith('video') || 
                    item.filename?.match(/\.(mp4|webm|mov|avi)$/i);
    
    if (isVideo) {
      const videoUrl = item.cloud_url || `${API.replace('/api', '')}/api/media/${item.id}`;
      return (
        <div className="w-full h-full relative bg-black">
          <video 
            src={videoUrl}
            className={`w-full h-full object-cover ${isSelected ? 'opacity-70' : ''}`}
            muted
            playsInline
            webkit-playsinline="true"
            preload="metadata"
            onMouseEnter={(e) => e.target.play()}
            onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
            onTouchStart={(e) => e.target.play()}
            onTouchEnd={(e) => { e.target.pause(); e.target.currentTime = 0; }}
          />
          {/* Video indicator overlay */}
          <div className="absolute bottom-1 right-1 bg-black/70 px-1.5 py-0.5 rounded text-xs flex items-center gap-1">
            <Video className="w-3 h-3 text-[#D4AF37]" />
          </div>
          {selectMode && (
            <div className={`absolute top-2 left-2 w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
              isSelected ? 'bg-[#D4AF37] border-[#D4AF37]' : 'border-white/50 bg-black/30'
            }`}>
              {isSelected && <Check className="w-3 h-3 text-black" />}
            </div>
          )}
        </div>
      );
    }
    if (item.content_type === 'application/pdf') {
      return (
        <div className="w-full h-full flex items-center justify-center bg-red-900/30">
          <FileText className="w-8 h-8 text-red-400" />
        </div>
      );
    }
    
    const imgUrl = item.cloud_url || `${API.replace('/api', '')}/api/media/${item.id}`;
    
    return (
      <>
        <img 
          src={imgUrl}
          alt={item.filename}
          className={`w-full h-full object-cover transition-all ${isSelected ? 'opacity-70' : ''}`}
          loading="lazy"
        />
        {selectMode && (
          <div className={`absolute top-2 left-2 w-6 h-6 rounded flex items-center justify-center ${isSelected ? 'bg-[#D4AF37]' : 'bg-black/50 border border-white/30'}`}>
            {isSelected && <Check className="w-4 h-4 text-black" />}
          </div>
        )}
      </>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="media-manager">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Image className="w-6 h-6 text-[#D4AF37]" />
            Media Bibliotheek ({media.length})
          </h2>
          <p className="text-gray-400 text-sm">Beheer foto's, video's en documenten</p>
        </div>
        
        <div className="flex gap-2">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept="image/*,video/*,application/pdf"
            multiple
            className="hidden"
          />
          <Button 
            onClick={() => setSelectMode(!selectMode)}
            variant={selectMode ? "secondary" : "outline"}
            className="border-white/10"
            data-testid="toggle-select-mode"
          >
            {selectMode ? <CheckSquare className="w-4 h-4 mr-2" /> : <Square className="w-4 h-4 mr-2" />}
            {selectMode ? `${selectedItems.size} geselecteerd` : 'Selecteren'}
          </Button>
          <Button 
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="bg-[#D4AF37] hover:bg-[#B8963A] text-black"
            data-testid="upload-media-btn"
          >
            <Upload className={`w-4 h-4 mr-2 ${uploading ? 'animate-pulse' : ''}`} />
            {uploading ? 'Uploaden...' : 'Upload'}
          </Button>
        </div>
      </div>

      {/* Selection Actions Bar */}
      {selectMode && selectedItems.size > 0 && (
        <div className="flex items-center gap-4 p-4 bg-[#D4AF37]/10 rounded-lg border border-[#D4AF37]/30">
          <span className="text-sm font-medium">{selectedItems.size} items geselecteerd</span>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={selectAll} className="border-white/10">
            Alles selecteren
          </Button>
          <Button size="sm" variant="outline" onClick={clearSelection} className="border-white/10">
            <X className="w-4 h-4 mr-1" /> Deselecteren
          </Button>
          <Button size="sm" onClick={() => setBulkAssignDialog(true)} className="bg-[#D4AF37] text-black">
            <Copy className="w-4 h-4 mr-1" /> Toevoegen aan pakket
          </Button>
          <Button size="sm" variant="destructive" onClick={handleBulkDelete}>
            <Trash2 className="w-4 h-4 mr-1" /> Verwijderen
          </Button>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Zoek op naam of categorie..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-white/5 border-white/10"
            data-testid="media-search-input"
          />
        </div>
        
        {/* Folder Filter */}
        <Select value={selectedFolder} onValueChange={setSelectedFolder}>
          <SelectTrigger className="w-[160px] bg-white/5 border-white/10">
            <FolderOpen className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Map" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/10">
            <SelectItem value="all">Alle mappen ({media.length})</SelectItem>
            {FOLDERS.map(folder => (
              <SelectItem key={folder} value={folder}>
                {folder} ({folderCounts[folder] || 0})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Category Filter */}
        <Select value={selectedCategory} onValueChange={setSelectedCategory}>
          <SelectTrigger className="w-[160px] bg-white/5 border-white/10">
            <Tag className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Categorie" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/10">
            {CATEGORIES.map(cat => (
              <SelectItem key={cat} value={cat}>
                {cat === 'all' ? 'Alle categorieën' : cat} ({categoryCounts[cat] || 0})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Sort */}
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-[140px] bg-white/5 border-white/10">
            <SortAsc className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Sorteren" />
          </SelectTrigger>
          <SelectContent className="bg-[#0A0A0A] border-white/10">
            <SelectItem value="newest">Nieuwste</SelectItem>
            <SelectItem value="oldest">Oudste</SelectItem>
            <SelectItem value="name">Naam</SelectItem>
            <SelectItem value="size">Grootte</SelectItem>
            <SelectItem value="category">Categorie</SelectItem>
          </SelectContent>
        </Select>
        
        {/* View Mode */}
        <div className="flex gap-1 bg-white/5 rounded-lg p-1">
          <Button
            variant={viewMode === 'grid' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('grid')}
          >
            <Grid className="w-4 h-4" />
          </Button>
          <Button
            variant={viewMode === 'list' ? 'secondary' : 'ghost'}
            size="sm"
            onClick={() => setViewMode('list')}
          >
            <List className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Media Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {filteredMedia.map(item => (
            <div 
              key={item.id}
              className={`relative group aspect-square bg-white/5 rounded-lg overflow-hidden border transition-all cursor-pointer ${
                selectedItems.has(item.id) 
                  ? 'border-[#D4AF37] ring-2 ring-[#D4AF37]/50' 
                  : 'border-white/10 hover:border-white/30'
              }`}
              onClick={() => selectMode ? toggleSelectItem(item.id) : null}
              data-testid={`media-item-${item.id}`}
            >
              {renderMediaThumbnail(item)}
              
              {/* Category Badge */}
              {item.category && item.category !== 'general' && (
                <div className="absolute top-2 right-2 px-2 py-0.5 bg-black/70 rounded text-xs text-[#D4AF37]">
                  {item.category}
                </div>
              )}
              
              {/* Overlay */}
              {!selectMode && (
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col justify-between p-2">
                  <div className="flex justify-end gap-1">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={(e) => { e.stopPropagation(); toggleSelectItem(item.id); setSelectMode(true); }}
                      className="h-7 w-7 p-0"
                    >
                      <CheckSquare className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={(e) => { e.stopPropagation(); deleteMedia(item.id); }}
                      className="h-7 w-7 p-0"
                    >
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                  <div>
                    <p className="text-xs truncate">{item.filename}</p>
                    <p className="text-xs text-[#D4AF37]">{item.folder}</p>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {filteredMedia.map(item => (
            <div 
              key={item.id}
              className={`flex items-center gap-4 p-3 bg-white/5 rounded-lg border transition-all cursor-pointer ${
                selectedItems.has(item.id) 
                  ? 'border-[#D4AF37] bg-[#D4AF37]/5' 
                  : 'border-white/10 hover:border-white/20'
              }`}
              onClick={() => selectMode ? toggleSelectItem(item.id) : null}
              data-testid={`media-item-${item.id}`}
            >
              {selectMode && (
                <div className={`w-5 h-5 rounded flex items-center justify-center ${selectedItems.has(item.id) ? 'bg-[#D4AF37]' : 'border border-white/30'}`}>
                  {selectedItems.has(item.id) && <Check className="w-3 h-3 text-black" />}
                </div>
              )}
              <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0 relative">
                {renderMediaThumbnail(item)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{item.filename}</p>
                <p className="text-xs text-gray-400">
                  {item.folder} • {item.category || 'general'} • {item.content_type}
                </p>
              </div>
              {!selectMode && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => { e.stopPropagation(); deleteMedia(item.id); }}
                  className="text-red-400 hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      {filteredMedia.length === 0 && (
        <div className="text-center py-12 text-gray-400">
          {searchQuery || selectedFolder !== 'all' || selectedCategory !== 'all'
            ? 'Geen media gevonden met deze filters' 
            : 'Nog geen media geüpload'}
        </div>
      )}

      {/* Bulk Assign Dialog */}
      <Dialog open={bulkAssignDialog} onOpenChange={setBulkAssignDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-[#D4AF37]" />
              {selectedItems.size} items toevoegen aan pakket
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <label className="text-sm text-gray-400 mb-2 block">Doelmap/Pakket</label>
              <Select value={targetFolder} onValueChange={setTargetFolder}>
                <SelectTrigger className="bg-white/5 border-white/10">
                  <SelectValue placeholder="Selecteer map..." />
                </SelectTrigger>
                <SelectContent className="bg-[#0A0A0A] border-white/10">
                  {FOLDERS.map(folder => (
                    <SelectItem key={folder} value={folder}>{folder}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={copyMode}
                  onChange={() => setCopyMode(true)}
                  className="text-[#D4AF37]"
                />
                <Copy className="w-4 h-4" />
                <span className="text-sm">Kopiëren (origineel blijft)</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  checked={!copyMode}
                  onChange={() => setCopyMode(false)}
                  className="text-[#D4AF37]"
                />
                <Move className="w-4 h-4" />
                <span className="text-sm">Verplaatsen</span>
              </label>
            </div>

            <div className="flex gap-2 pt-4">
              <Button
                onClick={() => setBulkAssignDialog(false)}
                variant="outline"
                className="flex-1 border-white/10"
              >
                Annuleren
              </Button>
              <Button
                onClick={handleBulkAssign}
                disabled={!targetFolder}
                className="flex-1 bg-[#D4AF37] hover:bg-[#B8963A] text-black"
              >
                {copyMode ? 'Kopiëren' : 'Verplaatsen'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
